import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Alert, Snackbar, Stack } from "@mui/material";
import { UserContext, useUser } from "../context/UserContext";
import DashboardHeader from "../components/DashboardComponents/DashboardHeader";
import DashboardTabs from "../components/DashboardComponents/DashboardTabs";
import DashboardEventsTab from "../components/DashboardComponents/DashboardEventsTab";
import DashboardPostsTab from "../components/DashboardComponents/DashboardPostsTab";
import UsersUpcomingElections from "../components/UpcomingElectionsComponents/UsersUpcomingElections";
import UserBadges from "../components/DashboardComponents/UserBadges";
import OpenDataActionModal from "../components/DashboardComponents/OpenDataActionModal";
import ReportModal from "../components/AlertComponents/ReportModal";
import DeleteConfirmModal from "../components/AlertComponents/DeleteConfirmModal";
import { blockUser, BLOCK_CONFIRM_DESCRIPTION } from "../utils/discoverApi";
import AuthRequiredModal from "../components/AlertComponents/AuthRequiredModal";
import VerifyIdentityModal from "../components/VerifyIdentityModal";
// import RepresentativeTopDonors from "../components/DashboardComponents/RepresentativeTopDonors";
import FollowUpActionsSection from "../components/DashboardComponents/FollowUpActionsSection";
import NearbyEventsLink from "../components/DashboardComponents/NearbyEventsLink";
import OpenCommentCard from "../components/DashboardComponents/OpenCommentCard";
import OpenCivicDataCard from "../components/DashboardComponents/OpenCivicDataCard";
import InviteSomeoneCard from "../components/DashboardComponents/InviteSomeoneCard";
import MemberCard from "../components/DashboardComponents/MemberCard";
import { theme } from "../theme/colors";
import { fetchUserEvents } from "../utils/eventApi";
import {
  deletePost,
  fetchUserDrafts,
  fetchUserPosts,
  likePost,
  logImpact,
  reportPost,
  unlikePost,
} from "../utils/dashboardPostsApi";
import {
  isValidBioguideId,
  memberPositionFromDashboardRole,
} from "../utils/billsMemberFilter";

const CONTAINER_CLASSES = "max-w-screen-2xl xl:max-w-[1400px] 2xl:max-w-[1600px]";

function SectionCard({ title, subtitle, children, className = "", centerTitle = false, hideBullet = false }) {
  return (
    <section
      className={`rounded-2xl border border-slate-200 bg-white/80 backdrop-blur p-5 shadow-sm ${className}`}
    >
      <div className={`flex items-start gap-2 ${centerTitle ? "flex-col items-center text-center" : ""}`}>
        {!hideBullet && (
          <span
            className="mt-2 inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: theme.primary }}
            aria-hidden="true"
          />
        )}
        <div className={centerTitle ? "w-full" : ""}>
          <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900">
            {title}
          </h2>
          {subtitle && <p className="mt-0.5 text-sm text-slate-900">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-2 [&>*:first-child]:mt-0 [&>*:first-child]:pt-0">{children}</div>
    </section>
  );
}

function CivicSidebar({ onOpenOpenDataModal, onInviteNotify, userZipcode, userState, showNearbyEvents }) {
  return (
    <aside className="col-span-12 xl:col-span-4 2xl:col-span-4 space-y-4">
      {showNearbyEvents && (
        <NearbyEventsLink zipcode={userZipcode} stateAbbr={userState} />
      )}
      <OpenCommentCard />
      <OpenCivicDataCard onOpenModal={onOpenOpenDataModal} />
      <InviteSomeoneCard onNotify={onInviteNotify} />
    </aside>
  );
}

function DashboardFooter() {
  return (
    <>
      <div className="mt-10 h-px w-full rounded bg-slate-200" />
      <footer className="py-6 text-center text-xs text-slate-900">
        <span className="font-semibold" style={{ color: theme.primary }}>
          Civix
        </span>
        <span className="font-semibold" style={{ color: theme.primary }}>
          Thread
        </span>{" "}
        • Civic participation made simple
      </footer>
    </>
  );
}

export default function DashboardPage() {
  const ctx = useContext(UserContext) ?? {};
  const userId = ctx.userId ?? null;
  const loading = ctx.loadingUser ?? false;

  const [openOpenDataModal, setOpenOpenDataModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [postToReport, setPostToReport] = useState(null);
  const [authorToBlock, setAuthorToBlock] = useState(null);
  
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("dashboardActiveTab") || "civic";
  });
  
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    localStorage.setItem("dashboardActiveTab", newTab);
  };

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    userRepsName,
    userRepsBioguideID,
    userState,
    userZipcode,
    userDistrict,
    userSenOneName,
    userSenOneBioguideID,
    userSenTwoName,
    userSenTwoBioguideID,
    accountTypeName,
    verifiedUser,
    canCreateEvent,
  } = useUser();

  const isLoggedIn = !!user;
  const isVerified = typeof verifiedUser === "boolean" ? verifiedUser : false;

  const [userPosts, setUserPosts] = useState([]);
  const [userDrafts, setUserDrafts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [postActionLoading, setPostActionLoading] = useState(false);

  const [userEvents, setUserEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [eventsFilter, setEventsFilter] = useState("upcoming");

  const showEventsTab = Boolean(canCreateEvent || accountTypeName);

  useEffect(() => {
    if (!showEventsTab && activeTab === "events") {
      handleTabChange("civic");
    }
  }, [activeTab, showEventsTab]);


  const showToast = useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const handleCreatePostClick = useCallback(() => {
    if (!isVerified) {
      setShowVerifyModal(true);
      return;
    }
    navigate("/community/create");
  }, [isVerified, navigate]);

  const loadUserPosts = useCallback(async () => {
    if (!userId) return;
    try {
      setPostsLoading(true);
      setPostsError(null);
      const [postsResponse, draftsResponse] = await Promise.all([
        fetchUserPosts(userId),
        fetchUserDrafts(userId),
      ]);
      setUserPosts(postsResponse?.posts || []);
      setUserDrafts(draftsResponse?.posts || []);
    } catch (err) {
      setPostsError(err?.response?.data?.error || err?.message || "Failed to load your posts.");
      setUserPosts([]);
      setUserDrafts([]);
    } finally {
      setPostsLoading(false);
    }
  }, [userId]);

  const loadUserEvents = useCallback(async () => {
    if (!userId || !showEventsTab) return;
    try {
      setEventsLoading(true);
      setEventsError(null);
      const events = await fetchUserEvents(userId, { includePast: true });
      setUserEvents(Array.isArray(events) ? events : []);
    } catch (err) {
      setEventsError(err?.message || "Failed to load your events.");
      setUserEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [userId, showEventsTab]);

  useEffect(() => {
    if (activeTab === "posts") {
      loadUserPosts();
    }
  }, [activeTab, loadUserPosts]);

  useEffect(() => {
    if (activeTab === "events") {
      loadUserEvents();
    }
  }, [activeTab, loadUserEvents]);

  // After saving a draft from create page: show toast, switch to Posts tab, clear state
  useEffect(() => {
    if (location.state?.draftSaved) {
      showToast("Draft saved.");
      handleTabChange("posts");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.draftSaved, location.pathname, navigate, showToast]);

  const handleToggleLike = useCallback(
    async (post) => {
      try {
        setPostActionLoading(true);
        const viewerHasLiked =
          post.viewer_has_upvoted || post.viewer_has_liked || post.viewerHasUpvoted;
        if (viewerHasLiked) {
          await unlikePost(post.id);
        } else {
          await likePost(post.id);
        }
        await loadUserPosts();
      } catch (err) {
        showToast(err?.response?.data?.error || err?.message || "Failed to update like.", "error");
      } finally {
        setPostActionLoading(false);
      }
    },
    [loadUserPosts, showToast]
  );

  const handleDeleteDraft = useCallback(
    async (draft) => {
      try {
        setPostActionLoading(true);
        await deletePost(draft.id);
        setUserDrafts((prev) => prev.filter((item) => item.id !== draft.id));
        showToast("Draft discarded.");
      } catch (err) {
        showToast(err?.response?.data?.error || err?.message || "Failed to discard draft.", "error");
        throw err; // Re-throw so the modal knows it failed
      } finally {
        setPostActionLoading(false);
      }
    },
    [showToast]
  );

  const handleEditDraft = useCallback(
    (draft) => {
      navigate("/community/create", { state: { editPostId: draft.id } });
    },
    [navigate]
  );

  const handleDeletePost = useCallback(
    async (post) => {
      try {
        setPostActionLoading(true);
        await deletePost(post.id);
        setUserPosts((prev) => prev.filter((item) => item.id !== post.id));
        showToast("Post deleted.");
      } catch (err) {
        showToast(err?.response?.data?.error || err?.message || "Failed to delete post.", "error");
        throw err; // Re-throw so the modal knows it failed
      } finally {
        setPostActionLoading(false);
      }
    },
    [showToast]
  );

  const handleReportPost = useCallback((post) => {
    setPostToReport(post);
    setShowReportModal(true);
  }, []);

  const handleBlockAuthor = useCallback((post) => {
    const author = post.user || post.author || {};
    const authorId = author.id ?? post.user_id;
    if (!authorId) return;
    const name =
      author.entity_name ||
      `${author.first_name || ""} ${author.last_name || ""}`.trim() ||
      author.username ||
      "this user";
    setAuthorToBlock({ id: authorId, name });
  }, []);

  const handleBlockConfirm = useCallback(async () => {
    if (!authorToBlock) return;
    try {
      setPostActionLoading(true);
      await blockUser(authorToBlock.id);
      setUserPosts((prev) =>
        prev.filter((item) => (item.user?.id ?? item.user_id) !== authorToBlock.id)
      );
      showToast(`${authorToBlock.name} has been blocked.`);
      setAuthorToBlock(null);
    } catch (err) {
      showToast(err?.message || "Couldn't block this account. Please try again.", "error");
    } finally {
      setPostActionLoading(false);
    }
  }, [authorToBlock, showToast]);

  const handleReportSubmit = useCallback(
    async (reason, details) => {
      if (!postToReport) return;
      if (!isLoggedIn) {
        setShowReportModal(false);
        setPostToReport(null);
        setShowAuthModal(true);
        return;
      }
      if (!isVerified) {
        setShowReportModal(false);
        setPostToReport(null);
        setShowVerifyModal(true);
        return;
      }
      try {
        setPostActionLoading(true);
        await reportPost(postToReport.id, reason, details);
        setShowReportModal(false);
        setPostToReport(null);
        showToast("Post reported.");
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || "Failed to report post.";
        if (err?.response?.status === 403 || msg === "Account verification required") {
          setShowReportModal(false);
          setPostToReport(null);
          setShowVerifyModal(true);
        } else {
          showToast(msg, "error");
        }
        throw err;
      } finally {
        setPostActionLoading(false);
      }
    },
    [postToReport, showToast, isLoggedIn, isVerified]
  );

  const handleLogImpact = useCallback(
    async (post) => {
      try {
        setPostActionLoading(true);
        await logImpact(post);
        showToast("Impact logged.");
        await loadUserPosts();
      } catch (err) {
        showToast(
          err?.code === "missing_action_type"
            ? "This post does not support action logging."
            : err?.response?.data?.error ||
              err?.message ||
              "Failed to log that action.",
          "error"
        );
      } finally {
        setPostActionLoading(false);
      }
    },
    [loadUserPosts, showToast]
  );

  const allRepsMissing =
    !isValidBioguideId(userRepsBioguideID) &&
    !isValidBioguideId(userSenOneBioguideID) &&
    !isValidBioguideId(userSenTwoBioguideID);

  const goToLegislation = useCallback(
    (bioguideId, role, memberName) => {
      if (!isValidBioguideId(bioguideId)) {
        showToast(
          "We don't have this representative's record yet. Try updating your address in account settings.",
          "info"
        );
        return;
      }
      navigate("/bills", {
        state: {
          memberBioguideId: bioguideId,
          memberName: memberName || role,
          memberPosition: memberPositionFromDashboardRole(role),
          clearFilters: true,
        },
      });
    },
    [navigate, showToast]
  );
  const districtLabel = userDistrict && userDistrict !== "N/A" ? userDistrict : "N/A";
  const stateLabel = userState && userState !== "N/A" ? userState : "State";
  const isDCResident = userState?.toUpperCase() === "DC";
  const repName =
    userRepsName && userRepsName !== "NOT FOUND" ? userRepsName : (isDCResident ? "Delegate" : "Representative");

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <p className="text-slate-600">Loading user...</p>
      </div>
    );
  }

  if (!user || !userId) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <p className="text-slate-600">Unable to load user. Please try again or sign in.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <main className={`mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 ${CONTAINER_CLASSES}`}>
        <header className="mb-4 sm:mb-6">
          <DashboardHeader />
          <DashboardTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            showEventsTab={showEventsTab}
          />
        </header>

        {activeTab === "civic" && (
          <div className="grid grid-cols-12 gap-6 xl:gap-8 items-start">
            <div className="col-span-12 xl:col-span-8 2xl:col-span-8 space-y-6">
              {!allRepsMissing && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  sx={{ mb: 0 }}
                  className="xl:max-w-[980px] 2xl:max-w-[1100px]"
                >
                  <MemberCard
                    bioguideId={userRepsBioguideID}
                    name={repName}
                    role={isDCResident ? "Delegate" : "Representative"}
                    regionLabel={districtLabel}
                    party={undefined}
                    onViewLegislation={() => goToLegislation(userRepsBioguideID, "Representative", repName)}
                  />
                  {!isDCResident && (
                    <>
                      <MemberCard
                        bioguideId={userSenOneBioguideID}
                        name={userSenOneName || "Senator"}
                        role="Senator"
                        regionLabel={stateLabel}
                        party={undefined}
                        onViewLegislation={() =>
                          goToLegislation(userSenOneBioguideID, "Senator1", userSenOneName)
                        }
                      />
                      <MemberCard
                        bioguideId={userSenTwoBioguideID}
                        name={userSenTwoName || "Senator"}
                        role="Senator"
                        regionLabel={stateLabel}
                        party={undefined}
                        onViewLegislation={() =>
                          goToLegislation(userSenTwoBioguideID, "Senator2", userSenTwoName)
                        }
                      />
                    </>
                  )}
                </Stack>
              )}
              <FollowUpActionsSection />

              <SectionCard
                title="Your Upcoming Elections"
                subtitle="Personalized based on your address."
                className="xl:max-w-[980px] 2xl:max-w-[1100px]"
                centerTitle
                hideBullet
              >
                <UsersUpcomingElections />
              </SectionCard>

              {/* Top Donors section hidden for now
              {userRepsName && userState && (
                <SectionCard
                  title="Top Donors to Your Representative"
                  subtitle="Campaign contributions from the current election cycle."
                  className="xl:max-w-[980px] 2xl:max-w-[1100px]"
                >
                  <RepresentativeTopDonors
                    representativeName={userRepsName}
                    state={userState}
                    cycle="2024"
                  />
                </SectionCard>
              )}
              */}

            </div>

            <CivicSidebar
              onOpenOpenDataModal={() => setOpenOpenDataModal(true)}
              onInviteNotify={showToast}
              userZipcode={userZipcode}
              userState={userState}
              showNearbyEvents={
                !allRepsMissing && Boolean(userZipcode || (userState && userState !== "N/A"))
              }
            />
          </div>
        )}

        {activeTab === "posts" && (
          <DashboardPostsTab
            posts={userPosts}
            drafts={userDrafts}
            currentUserId={userId}
            loading={postsLoading}
            error={postsError}
            actionLoading={postActionLoading}
            onRetry={loadUserPosts}
            onToggleLike={handleToggleLike}
            onDeleteDraft={handleDeleteDraft}
            onEditDraft={handleEditDraft}
            onDeletePost={handleDeletePost}
            onReportPost={handleReportPost}
            onBlockAuthor={handleBlockAuthor}
            onLogImpact={handleLogImpact}
            onCreatePost={handleCreatePostClick}
          />
        )}

        {activeTab === "badges" && (
          <section className="xl:max-w-[980px] 2xl:max-w-[1100px] mx-auto text-center w-full">
            <UserBadges />
          </section>
        )}

        {activeTab === "events" && showEventsTab && (
          <DashboardEventsTab
            events={userEvents}
            loading={eventsLoading}
            error={eventsError}
            filter={eventsFilter}
            onFilterChange={setEventsFilter}
            onRetry={loadUserEvents}
            canCreateEvent={Boolean(canCreateEvent || accountTypeName)}
          />
        )}

        <OpenDataActionModal
          open={openOpenDataModal}
          onClose={() => setOpenOpenDataModal(false)}
        />

        <ReportModal
          open={showReportModal}
          contentType="post"
          onClose={() => {
            setShowReportModal(false);
            setPostToReport(null);
          }}
          onSubmit={handleReportSubmit}
        />

        <DeleteConfirmModal
          open={Boolean(authorToBlock)}
          onClose={() => setAuthorToBlock(null)}
          onConfirm={handleBlockConfirm}
          title={`Block ${authorToBlock?.name || "this user"}?`}
          description={BLOCK_CONFIRM_DESCRIPTION}
          confirmLabel="Block"
          loading={postActionLoading}
        />

        <AuthRequiredModal
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />

        <VerifyIdentityModal
          isOpen={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
        />

        <DashboardFooter />
      </main>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
